import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { ExternalLink, CalendarDays } from "lucide-react";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";

export default function NewsDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  // V9.11 — Si l'URL commence par "contrib-", on charge un article contributeur via l'endpoint dédié.
  const isContrib = (slug || "").startsWith("contrib-");
  const realSlug = isContrib ? slug.slice("contrib-".length) : slug;

  useEffect(() => {
    setLoading(true);
    const url = isContrib ? `/contrib/articles/${realSlug}` : `/news/${realSlug}`;
    api.get(url).then((r) => setItem(r.data)).catch(() => setItem(null)).finally(() => setLoading(false));
  }, [realSlug, isContrib]);

  if (loading) return <div className="mx-auto max-w-3xl p-8"><Skeleton className="aspect-video w-full" /><Skeleton className="mt-4 h-10 w-3/4" /><Skeleton className="mt-2 h-4 w-1/2" /></div>;
  if (!item) return <div className="mx-auto max-w-3xl p-8">Actualité introuvable. <Link to="/actualites" className="text-[hsl(var(--aito-lagoon))]">Retour</Link></div>;

  const date = item.published_at ? new Date(item.published_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "";
  const cover = item.cover_image_url || item.cover_url || item.image_url;
  return (
    <article className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {item.discipline && <Badge variant="outline">{item.discipline}</Badge>}
        {item.is_official && <Badge className="bg-[hsl(var(--aito-gold))] text-black">Officiel</Badge>}
        {isContrib && <Badge className="bg-[hsl(var(--aito-lagoon))] text-black">Article contributeur</Badge>}
        {item.type && <Badge variant="secondary">{item.type}</Badge>}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight break-words">{item.title}</h1>
      <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
        <CalendarDays className="h-4 w-4" /> {date}
        {item.author_name && <> · par <strong>{item.author_name}</strong></>}
      </div>
      {cover && <img src={cover} alt="" className="mt-5 w-full rounded-2xl aspect-video object-cover" />}
      {item.summary && <p className="mt-5 text-lg leading-relaxed text-muted-foreground break-words">{item.summary}</p>}
      {/* V9.11 — Contributeurs : rendu Markdown ; sinon : texte plein */}
      {isContrib && item.body_md ? (
        <div className="mt-6"><MarkdownRenderer>{item.body_md}</MarkdownRenderer></div>
      ) : item.body ? (
        <div className="mt-5 prose max-w-none whitespace-pre-line text-[15px] leading-relaxed break-words">{item.body}</div>
      ) : null}
      {item.source_url && !isContrib && (
        <Card className="mt-8 p-4 bg-secondary/30">
          <div className="text-xs text-muted-foreground mb-1">Résumé généré à partir d'une source externe</div>
          <a href={item.source_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))] font-medium inline-flex items-center gap-1"><ExternalLink className="h-4 w-4" /> Lire la source originale</a>
        </Card>
      )}
    </article>
  );
}
